import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserCreditStatus } from '../services/creditService';
import { Spinner } from '@shopify/polaris';

export default function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth();
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasValidSubscription, setHasValidSubscription] = useState(true);
  const location = useLocation();

  // Check if the current path is the subscription page or credits page
  const isSubscriptionPage = location.pathname === '/app/subscription';
  const isCreditsPage = location.pathname === '/app/credits';
  const isAdminPanel = location.pathname === '/app/admin';
  
  // Pages that don't require a valid subscription
  const exemptPages = ['/app/subscription', '/app/credits', '/app/settings', '/app/payment-history'];
  const isExemptPage = exemptPages.some(path => location.pathname === path);

  useEffect(() => {
    async function checkSubscription() {
      // Skip subscription check for admin users accessing the admin panel
      if (isAdmin && isAdminPanel) {
        setHasValidSubscription(true);
        setSubscriptionChecked(true);
        return;
      }
      
      if (user && !isExemptPage) {
        try {
          // Use getUserCreditStatus instead of getUserSubscription to get VIP status
          const creditStatus = await getUserCreditStatus(user.uid);
          const { subscription, isVIP, vipPlan } = creditStatus;
          
          // Consider VIP status with a plan as a valid subscription
          const hasVipAccess = isVIP && vipPlan;
          
          // Check if subscription is expired or not active
          const isValid = (subscription && 
                        subscription.status && 
                        (subscription.status === 'active' || 
                         subscription.status === 'canceling') &&
                        subscription.status !== 'expired') || hasVipAccess;
          
          console.log('Subscription check:', { isValid, hasVipAccess, subscription, isVIP, vipPlan });
          setHasValidSubscription(isValid);
        } catch (error) {
          console.error('Error checking subscription:', error);
          setHasValidSubscription(false);
        }
      }
      setSubscriptionChecked(true);
    }

    if (user && !loading) {
      checkSubscription();
    }
  }, [user, loading, isExemptPage, isAdmin, isAdminPanel]);

  if (loading || (user && !subscriptionChecked)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!user) {
    // Don't redirect to auth if we're already on the register page or landing page
    const isRegisterPage = window.location.pathname === '/register';
    const isLandingPage = window.location.pathname === '/';
    const hasReferralCode = new URLSearchParams(window.location.search).get('ref');
    
    // If we're on the register page (especially with a referral code) or landing page, don't redirect
    if (isRegisterPage || isLandingPage) {
      console.log('On register page or landing page, not redirecting to auth');
      return children;
    }
    
    return <Navigate to="/app" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/app" />;
  }

  // If subscription check is complete and user doesn't have a valid subscription,
  // redirect to subscription page (unless they're already there or they're an admin accessing the admin panel)
  if (subscriptionChecked && !hasValidSubscription && !isExemptPage && !(isAdmin && isAdminPanel)) {
    return <Navigate to="/app/subscription" state={{ from: location, expired: true }} />;
  }

  return children;
}
